/**
 * 儿童模式与学生端权限的**唯一判定入口**（见方案 2.5 与 shared/permissions.ts）。
 *
 * 刻意集中在一个文件里，原因有两个：
 *  1. 儿童模式的判定依据是**年级**，不是屏幕尺寸；一旦散落到各页面成 `if (isSmall)`
 *     就会退化成"手机就是儿童模式"这种错误实现。
 *  2. 学生端"家长创建的内容只能打卡"是权限问题，必须走 shared 的
 *     `canMutateContent` / `ownershipLabel`，不能在各页面自己写 `role === 'student'`。
 *
 * 判定优先级（与 api/students.ts 的 resolveUiMode 保持一致，这里做统一收口）：
 *   家长手动覆盖 uiModeOverride  >  后端返回的 uiMode  >  按年级推算
 */
import {
  canAdjustPoints,
  canCheckin,
  canCreateContent,
  canDecideRedemption,
  canManageRewards,
  canMutateContent,
  canUndoCheckin,
  gradeToUiMode,
  ownershipLabel,
  type Actor,
  type ContentOwnership,
  type UiMode,
} from '@fht/shared';

export interface UiModeSource {
  /** 学生档案上的年级 code（`primary_1` 等）。 */
  gradeCode?: string | null;
  /** 家长手动覆盖值；null 表示跟随年级。 */
  uiModeOverride?: UiMode | null;
  /** 后端在 `/student/today` 里返回的 uiMode。 */
  serverUiMode?: string | null;
}

/** 解析最终的界面模式。任何输入都返回合法的 UiMode，绝不返回 null（儿童模式不能"静默失效"）。 */
export function resolveUiMode(source: UiModeSource): UiMode {
  if (source.uiModeOverride === 'child' || source.uiModeOverride === 'bridge' || source.uiModeOverride === 'full') {
    return source.uiModeOverride;
  }
  if (source.serverUiMode === 'child' || source.serverUiMode === 'bridge' || source.serverUiMode === 'full') {
    return source.serverUiMode;
  }
  if (source.gradeCode) return gradeToUiMode(source.gradeCode);
  return 'full';
}

/** 儿童模式（小学 1~3）：大按钮、大字号、图标+文字、隐藏统计图表与导出、打卡最多 2 步。 */
export function isChildMode(mode: UiMode): boolean {
  return mode === 'child';
}

/** 过渡模式（小学 4~6）：可见目标树与简单进度，保留完整打卡。 */
export function isBridgeMode(mode: UiMode): boolean {
  return mode === 'bridge';
}

/** 是否允许展示统计图表与导出：儿童模式**明确隐藏**（见方案 2.5）。 */
export function canShowStats(mode: UiMode): boolean {
  return mode !== 'child';
}

/** 是否允许展示多级目标树：儿童模式只看"今天要做什么"。 */
export function canShowNodeTree(mode: UiMode): boolean {
  return mode !== 'child';
}

/** 儿童模式下的统一样式片段，避免每个页面各写一套尺寸。 */
export const MODE_TEXT = {
  child: 'text-lg',
  bridge: 'text-base',
  full: 'text-sm',
} as const;

const MODE_HINT: Record<UiMode, string> = {
  child: '儿童模式：大按钮 + 大图标，两步完成打卡',
  bridge: '过渡模式：按钮和文字放大一些，可以看到目标树',
  full: '标准模式：与家长端一致的信息密度（权限仍然受限）',
};

export function uiModeHint(mode: UiMode): string {
  return MODE_HINT[mode];
}

// ---------------------------------------------------------------- 权限
//
// 全部转发 shared 的实现。这里只负责"把当前登录者包装成 Actor"，
// 页面拿到的是布尔值，不接触角色字符串。

/** 当前登录者是不是学生本人。 */
export function isStudentActor(actor: Actor): boolean {
  return actor.kind === 'student';
}

/** 能否新建目标 / 打卡任务。 */
export function canCreate(actor: Actor): boolean {
  return canCreateContent(actor);
}

/**
 * 能否编辑 / 删除某条内容。
 * 学生对自己创建的可改可删；对**家长创建的一律 false** → 界面隐藏按钮。
 */
export function canEditContent(actor: Actor, content: ContentOwnership): boolean {
  return canMutateContent(actor, content);
}

/** 能否打卡 / 计时。 */
export function canDoCheckin(actor: Actor): boolean {
  return canCheckin(actor);
}

/** 撤销打卡**仅家长 owner/editor** —— 孩子界面不要出现撤销按钮。 */
export function canUndo(actor: Actor): boolean {
  return canUndoCheckin(actor);
}

/** 手动调分：仅家长 owner/editor。 */
export function canAdjust(actor: Actor): boolean {
  return canAdjustPoints(actor);
}

/** 维护奖品目录：仅家长 owner/editor。 */
export function canManageReward(actor: Actor): boolean {
  return canManageRewards(actor);
}

/** 审批 / 履约兑换：仅家长 owner/editor。 */
export function canDecideRedeem(actor: Actor): boolean {
  return canDecideRedemption(actor);
}

/** 归属标签："家长创建" / "我创建的" / "学生创建"。直接来自 shared。 */
export function ownershipText(content: ContentOwnership, actor: Actor): string {
  return ownershipLabel(content, actor);
}

/**
 * 学生只能打卡的提示语（见契约 3：界面上要写明"家长创建，只能打卡"）。
 * 返回 null 表示这条内容学生可以自己改，不需要提示。
 */
export function readOnlyHint(
  actor: Actor,
  content: ContentOwnership | undefined,
): string | null {
  if (!content) return null;
  if (canMutateContent(actor, content)) return null;
  if (actor.kind === 'student' && content.createdByType === 'parent') {
    return '家长创建，只能打卡';
  }
  return '你只能查看，不能修改';
}

/** 把学生档案上的 createdByType/createdById 收敛成 ContentOwnership。 */
export function ownershipOf(node: {
  createdByType?: 'parent' | 'student';
  createdById?: string;
}): ContentOwnership {
  return {
    createdByType: node.createdByType === 'student' ? 'student' : 'parent',
    createdById: node.createdById ?? '',
  };
}
